import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Mic } from "lucide-react";
import { useLang } from "../i18n";
import { FAQ, answerFromFaq } from "../data/faq";

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  const faqs = FAQ[lang] || FAQ.en;

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat_greeting") }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const respond = (text) => {
    const answer = answerFromFaq(text, lang);
    setMessages((m) => [
      ...m,
      { role: "user", content: text },
      { role: "assistant", content: answer || t("chat_fallback"), suggest: !answer },
    ]);
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    respond(text);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const showChipsFor = (idx) => idx === 0 || messages[idx]?.suggest;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-5 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl shadow-black/60 flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-800 bg-zinc-950/60">
              <div className="w-9 h-9 rounded-xl bg-emerald-600/20 flex items-center justify-center shrink-0">
                <Mic size={18} className="text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white leading-tight">{t("chat_title")}</p>
                <p className="text-xs text-zinc-500 truncate">{t("chat_sub")}</p>
              </div>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)}
                className="ml-auto text-zinc-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={`msg-${i}-${m.role}`}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-emerald-600 text-white rounded-br-sm"
                        : "bg-zinc-800 text-zinc-200 rounded-bl-sm"}`}>
                      {m.content}
                    </div>
                  </div>
                  {m.role === "assistant" && showChipsFor(i) && (
                    <div data-testid="chat-suggestions" className="flex flex-wrap gap-1.5 mt-2">
                      {faqs.map((f) => (
                        <button key={f.q} data-testid="chat-suggestion-chip" onClick={() => respond(f.q)}
                          className="text-[11px] text-emerald-300 border border-emerald-800/60 hover:bg-emerald-950/40 rounded-full px-2.5 py-1 transition-colors text-left">
                          {f.q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-zinc-800 flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder={t("chat_ph")}
                className="flex-1 resize-none bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-white max-h-28 focus:outline-none focus:border-emerald-600" />
              <button data-testid="chat-send-btn" onClick={send} disabled={!input.trim()}
                className="w-10 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 flex items-center justify-center text-white transition-colors shrink-0">
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-toggle-btn"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((o) => !o)}
        aria-label={t("chat_open")}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/40 flex items-center justify-center transition-colors">
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </motion.button>
    </>
  );
};
